import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Loader2, Search, X } from "lucide-react";
import { getBankingTraders } from "../../../../Rest_api/RestApi";

/**
 * Strict select dropdown for the contract Trader Name, with search.
 * Options come from GET /banking_cspdcl/traders. Free text is not allowed:
 * every chosen trader is always one of the API-provided options.
 *
 * Single-select (default): `value` is "" or one trader name.
 * Multi-select (`multiple`): `value` is a comma-joined list of trader names
 * (e.g. "A, B"), so the field stays a plain string for storage/submission.
 */
export function TraderSelect({
  value,
  onChange,
  placeholder = "Select a trader",
  error = false,
  disabled = false,
  triggerClassName = "",
  multiple = false,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  triggerClassName?: string;
  multiple?: boolean;
}) {
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // In multi-select mode the value is a comma-joined list; parse it into the
  // individual selected trader names (single mode treats `value` as one entry).
  const selectedValues = useMemo(
    () =>
      multiple
        ? value
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : value
          ? [value]
          : [],
    [value, multiple],
  );
  const selectedSet = useMemo(
    () => new Set(selectedValues.map((s) => s.toLowerCase())),
    [selectedValues],
  );
  const isSelected = (opt: string) => selectedSet.has(opt.toLowerCase());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    getBankingTraders()
      .then((list) => {
        if (!cancelled) setOptions(list);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Once the list loads, reconcile the incoming value with it: canonicalize
  // casing (e.g. an extracted "cspdcl" → "CSPDCL"), or clear it entirely if it
  // isn't a known trader. Keeps `value` strictly in-list so required-validation
  // upstream stays correct.
  useEffect(() => {
    if (!options.length || !value) return;
    if (multiple) {
      // Canonicalize every token to its in-list spelling, drop unknowns and
      // duplicates, then rejoin. Keeps the stored string strictly in-list.
      const seen = new Set<string>();
      const canon: string[] = [];
      for (const token of selectedValues) {
        const match = options.find(
          (o) => o.toLowerCase() === token.toLowerCase(),
        );
        if (match && !seen.has(match.toLowerCase())) {
          seen.add(match.toLowerCase());
          canon.push(match);
        }
      }
      const next = canon.join(", ");
      if (next !== value) onChange(next);
      return;
    }
    const match = options.find((o) => o.toLowerCase() === value.toLowerCase());
    if (match && match !== value) onChange(match);
    else if (!match) onChange("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    const sel = options.findIndex((o) => isSelected(o));
    setActiveIndex(sel >= 0 ? sel : 0);
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-idx="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  const commit = (opt: string) => {
    if (multiple) {
      // Toggle the option in/out of the selection; keep the menu open so the
      // user can pick several at once.
      const next = isSelected(opt)
        ? selectedValues.filter((v) => v.toLowerCase() !== opt.toLowerCase())
        : [...selectedValues, opt];
      onChange(next.join(", "));
      searchRef.current?.focus();
      return;
    }
    onChange(opt);
    setOpen(false);
  };

  const removeOne = (opt: string) => {
    onChange(
      selectedValues
        .filter((v) => v.toLowerCase() !== opt.toLowerCase())
        .join(", "),
    );
    searchRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[activeIndex];
      if (opt) commit(opt);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    } else if (
      e.key === "Backspace" &&
      multiple &&
      !query &&
      selectedValues.length
    ) {
      // Empty search + backspace removes the most recently added chip.
      e.preventDefault();
      removeOne(selectedValues[selectedValues.length - 1]);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <style>{`
        @keyframes traderChipIn {
          from { opacity: 0; transform: scale(0.8) translateY(2px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes traderMenuIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .trader-chip-scroll { scrollbar-width: thin; scrollbar-color: #E6CBB7 transparent; }
        .trader-chip-scroll::-webkit-scrollbar { width: 6px; }
        .trader-chip-scroll::-webkit-scrollbar-thumb { background: #E6CBB7; border-radius: 9999px; }
        .trader-chip-scroll::-webkit-scrollbar-track { background: transparent; }
      `}</style>

      {multiple ? (
        // Chip-based combobox: selected traders render as removable tags that
        // wrap to new lines; the inline input both searches and grows the box.
        <div
          onMouseDown={(e) => {
            if (disabled) return;
            const el = e.target as HTMLElement;
            // Chip remove / chevron handle their own clicks; let the input
            // itself place the caret normally.
            if (el.closest("[data-stop]") || el === searchRef.current) return;
            e.preventDefault();
            setOpen(true);
            searchRef.current?.focus();
          }}
          className={`flex items-center gap-1.5 min-h-[40px] px-2 py-1.5 rounded-[12px] bg-white cursor-text border transition-all duration-200 ${
            error
              ? "border-[#C8311C] shadow-[0_0_0_3px_rgba(200,49,28,0.12)]"
              : open
                ? "border-[#F1935C] shadow-[0_0_0_3px_rgba(241,147,92,0.16)]"
                : "border-[#ECECEF] shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:border-[#E6CBB7]"
          } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        >
          <Search size={15} className="shrink-0 self-center text-[#B7B5C0] ml-1" />
          {/* Chips + inline search: capped to one row tall, scrolls vertically
              when more traders are selected than fit on the first line. */}
          <div className="flex-1 flex flex-wrap items-center gap-1.5 max-h-[26px] overflow-y-auto trader-chip-scroll pr-0.5">
            {selectedValues.map((t) => (
              <span
                key={t}
                style={{ animation: "traderChipIn 150ms ease-out" }}
                className="group inline-flex items-center gap-1 max-w-full pl-2.5 pr-1 py-[3px] rounded-full bg-[#FCEBDC] text-[#C2652F] text-[11px] font-medium leading-none transition-colors duration-150 hover:bg-[#FAE0CB]"
              >
                <span className="truncate">{t}</span>
                <button
                  type="button"
                  data-stop
                  disabled={disabled}
                  onClick={() => removeOne(t)}
                  aria-label={`Remove ${t}`}
                  className="shrink-0 grid place-items-center w-4 h-4 rounded-full text-[#C2652F]/70 transition-all duration-150 hover:text-white hover:bg-[#F1935C] active:scale-90"
                >
                  <X size={11} strokeWidth={2.5} />
                </button>
              </span>
            ))}
            <input
              ref={searchRef}
              value={query}
              disabled={disabled}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
                if (!open) setOpen(true);
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => setOpen(true)}
              placeholder={selectedValues.length ? "" : placeholder}
              className="flex-1 min-w-[80px] h-5 bg-transparent text-xs outline-none text-[#4B4954] placeholder:text-[#A8A6B2]"
            />
          </div>
          <button
            type="button"
            data-stop
            disabled={disabled}
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle options"
            className="shrink-0 grid place-items-center"
          >
            <ChevronDown
              size={15}
              className={`text-[#9B99A4] transition-transform duration-200 ${
                open ? "rotate-180" : ""
              }`}
            />
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen((o) => !o)}
          className={`${triggerClassName} flex items-center justify-between gap-2 text-left cursor-pointer ${
            error ? "border-[#C8311C] focus:border-[#C8311C]" : ""
          } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        >
          <span className={`truncate ${value ? "" : "text-[#A8A6B2]"}`}>
            {value || placeholder}
          </span>
          <ChevronDown
            size={14}
            className={`shrink-0 text-[#8C8A96] transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
      )}

      {open && (
        <div
          style={{ animation: "traderMenuIn 140ms ease-out" }}
          className="absolute left-0 right-0 top-full mt-1.5 z-50 bg-white border border-[#EEEFF2] rounded-[12px] shadow-[0_12px_32px_-8px_rgba(16,24,40,0.18)] overflow-hidden"
        >
          {!multiple && (
            <div className="flex items-center gap-2 px-2.5 border-b border-[#ECEEF3]">
              <Search size={13} className="text-[#A8A6B2] shrink-0" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
                onKeyDown={handleKeyDown}
                placeholder="Search trader…"
                className="w-full h-8 text-xs outline-none text-[#6F6D7C] placeholder:text-[#A8A6B2] bg-transparent"
              />
            </div>
          )}
          <div ref={listRef} className="max-h-52 overflow-y-auto py-1">
            {loading ? (
              <div className="flex items-center gap-2 px-3 py-2 text-[11px] text-[#8C8A96]">
                <Loader2 size={12} className="animate-spin" /> Loading traders…
              </div>
            ) : loadError ? (
              <div className="px-3 py-2 text-[11px] text-[#C0392B]">
                Failed to load traders.
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-2 text-[11px] text-[#8C8A96]">
                No traders found.
              </div>
            ) : (
              filtered.map((opt, i) => {
                const selected = isSelected(opt);
                const active = i === activeIndex;
                return (
                  <button
                    key={opt}
                    type="button"
                    data-idx={i}
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={() => commit(opt)}
                    className={`w-full flex items-center justify-between gap-2 text-left mx-1 px-2.5 py-1.5 rounded-lg text-xs cursor-pointer transition-colors duration-100 ${
                      active
                        ? "bg-[#FBEEE3] text-[#C2652F]"
                        : selected
                          ? "bg-[#FCF4ED] text-[#C2652F] font-medium"
                          : "text-[#6F6D7C] hover:bg-[#F7F7F9]"
                    } ${selected ? "font-medium" : ""}`}
                    style={{ width: "calc(100% - 0.5rem)" }}
                  >
                    <span className="truncate">{opt}</span>
                    {selected && (
                      <Check size={13} className="shrink-0 text-[#F1935C]" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TraderSelect;
